define([
    '../Util/defined',
    '../System/Vec3Obj',
    '../System/ColorObj',
    '../Enum/EnumAlignmentMode',
    '../Enum/EnumRotateMode',
    '../Enum/EnumZoomMode',
    '../Enum/EnumBackdropMode'
], function (
    defined,
    Vec3Obj,
    ColorObj,
    EnumAlignmentMode,
    EnumRotateMode,
    EnumZoomMode,
    EnumBackdropMode
) {
    'use strict'

    /**
     * 文字符号。
     * <br /><br />
     * <div align='center'>
     * <img src='images/TextSymbol.png' width='900' height='650' /><br />
     * 文字符号效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/TextSymbol.html|CooWasm TextSymbol demo}
     * @alias TextSymbol
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {EnumAlignmentMode} [props.alignMode=EnumAlignmentMode.CENTER_CENTER] 文字相对原点对齐方式。
     * @param {EnumRotateMode} [props.rotateMode=EnumRotateMode.TO_SCREEN] 文字朝向方式。
     * @param {EnumZoomMode} [props.zoomMode=EnumZoomMode.CAMERA] 文字缩放方式。
     * @param {Number} [props.zoomLimit=2000.0] 文字缩放上限值，单位：米。
     * @param {Number} [props.minDistance=1.0] 最小可视距离。
     * @param {Number} [props.maxDistance=20000.0] 最大可视距离。
     * @param {Number} [props.liftUp=0.0] 文字抬高高度，单位：米。
     * @param {Number} [props.scale=1] 文字缩放比例值。
     * @param {String} props.info 文字显示内容。
     * @param {String} [props.fontUrl="data/Fonts/arial.ttf"] 字体路径。
     * @param {Number} [props.size=32] 字体显示大小。
     * @param {ColorObj} [props.color=new ColorObj(1.0,1.0,1.0,1.0)] 字体颜色。
     * @param {Boolean} [props.bOutline=true] 是否开启轮廓线。
     * @param {ColorObj} [props.outlineColor=new ColorObj(0.0,0.0,0.0,1.0)] 轮廓线颜色。
     * @param {Number} [props.outlineWidth=1.0] 轮廓线宽度。
     * @param {Boolean} [props.bEmbolden=true] 文字是否加粗。
     * @param {Boolean} [props.bItalics=false] 文字是否倾斜显示。
     * @param {Boolean} [props.bUnderline=false] 文字是否显示下划线。
     * @param {ColorObj} [props.underlineColor=new ColorObj(1.0,0.0,0.0,1.0)] 下划线颜色。
     * @param {Boolean} [props.bHorizontal=true] 文字是否水平显示。
     * @param {Number} [props.letterSpacing=0.0] 文字间距。
     * @param {EnumBackdropMode} [props.backdropMode=EnumBackdropMode.NONE] 文字背景显示模式。
     * @param {ColorObj} [props.backdropColor=new ColorObj(0.0,0.0,1.0,0.6)] 背景颜色，仅在backdropMode为EnumBackdropMode.COLOR时生效。
     * @param {String} [props.backdropUrl] 背景图片路径，仅在backdropMode为EnumBackdropMode.TEXTURE时生效。
     * @param {Number} [props.backdropMarginTop=3.0] 背景上边距。
     * @param {Number} [props.backdropMarginBottom=3.0] 背景下边距。
     * @param {Number} [props.backdropMarginLeft=3.0] 背景左边距。
     * @param {Number} [props.backdropMarginRight=3.0] 背景右边距。
     * @example
     * var text = new CooWasm.TextSymbol({
     *     info:"text"
     * });
     */
    class TextSymbol {
        /**
         * @description 文字相对原点对齐方式。
         * @type {EnumAlignmentMode}
         */
        alignMode
        /**
         * @description 文字朝向方式。
         * @type {EnumRotateMode}
         */
        rotateMode
        /**
         * @description 文字缩放方式。
         * @type {EnumZoomMode}
         */
        zoomMode
        /**
         * @description 文字缩放上限值，单位：米。
         * @type {Number}
         */
        zoomLimit
        /**
         * @description 最小可视距离。
         * @type {Number}
         */
        minDistance
        /**
         * @description 最大可视距离。
         * @type {Number}
         */
        maxDistance
        /**
         * @description 文字抬高高度，单位：米。
         * @type {Number}
         */
        liftUp
        /**
         * @description 文字缩放比例值。
         * @type {Number}
         * @readonly
         */
        scale
        /**
         * @description 文字显示内容。
         * @type {String}
         */
        info
        /**
         * @description 字体路径。
         * @type {String}
         */
        fontUrl
        /**
         * @description 字体显示大小。
         * @type {Number}
         */
        size
        /**
         * @description 字体颜色。
         * @type {ColorObj}
         */
        color
        /**
         * @description 是否开启轮廓线。
         * @type {Boolean}
         * @default true
         */
        bOutline
        /**
         * @description 轮廓线颜色。
         * @type {ColorObj}
         */
        outlineColor
        /**
         * @description 轮廓线宽度。
         * @type {Number}
         */
        outlineWidth
        /**
         * @description 文字是否加粗。
         * @type {Boolean}
         */
        bEmbolden
        /**
         * @description 文字是否倾斜显示。
         * @type {Boolean}
         */
        bItalics
        /**
         * @description 文字是否显示下划线。
         * @type {Boolean}
         */
        bUnderline
        /**
         * @description 下划线颜色。
         * @type {ColorObj}
         */
        underlineColor
        /**
         * @description 文字是否水平显示。
         * @type {Boolean}
         */
        bHorizontal
        /**
         * @description 文字间距。
         * @type {Number}
         */
        letterSpacing
        /**
         * @description 文字背景显示模式。
         * @type {EnumBackdropMode}
         */
        backdropMode
        /**
         * @description 背景颜色，仅在backdropMode为EnumBackdropMode.COLOR时生效。
         * @type {ColorObj}
         */
        backdropColor
        /**
         * @description 背景图片路径，仅在backdropMode为EnumBackdropMode.TEXTURE时生效。
         * @type {String}
         */
        backdropUrl
        /**
         * @description 背景上边距。
         * @type {Number}
         */
        backdropMarginTop
        /**
         * @description 背景下边距。
         * @type {Number}
         */
        backdropMarginBottom
        /**
         * @description 背景左边距。
         * @type {Number}
         */
        backdropMarginLeft
        /**
         * @description 背景右边距。
         * @type {Number}
         */
        backdropMarginRight
        /**
         * @description 上方向轴。
         * @type {Vec3Obj}
         * @readonly
         */
        upAxis
        /**
         * @description 属性信息。
         * @private
         * @readonly
         */
        properties = ""
        /**
         * @description GeoSphere在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "TextSymbol"

        constructor(props) {
            if (!defined(props)) {
                props = {}
            }
            // if (!defined(props.info)) {
            //     console.log("need info!")
            //     return null;
            // }
            if (this.backdropMode == EnumBackdropMode.TEXTURE) {
                if (!defined(props.backdropUrl)) {
                    console.log("backdrop need image url!")
                    return null
                }
                this.properties += this.toStr("backdropUrl", props.backdropUrl, "BackdropImageUrl")
            }
            this.properties += this.toStr("info", props.info, "Info")
            //if(defined(props.alignMode)){
                this.properties += this.toStr("alignMode", props.alignMode, "AlignmentMode")
            //}
            this.properties += this.toStr("rotateMode", props.rotateMode, "RotateMode")
            this.properties += this.toStr("zoomMode", props.zoomMode, "ZoomMode")
            this.properties += this.toStr("zoomLimit", props.zoomLimit, "ZoomMaxDistance")
            this.properties += this.toStr("minDistance", props.minDistance, "MinVisibleDistance")
            this.properties += this.toStr("maxDistance", props.maxDistance, "MaxVisibleDistance")
            this.properties += this.toStr("liftUp", props.liftUp, "LiftUp")
            this.properties += this.toStr("scale", props.scale, "Scale")
            this.properties += this.toStr("fontUrl", props.fontUrl, "FontUrl")
            this.properties += this.toStr("size", props.size, "Size")
            this.properties += this.toStr("color", props.color, "Color")
            this.properties += this.toStr("bOutline", props.bOutline, "Outline")
            this.properties += this.toStr("outlineColor", props.outlineColor, "OutlineColor")
            this.properties += this.toStr("outlineWidth", props.outlineWidth, "OutlineWidth")
            this.properties += this.toStr("bEmbolden", props.bEmbolden, "Embolden")
            this.properties += this.toStr("bItalics", props.bItalics, "Italics")
            this.properties += this.toStr("bUnderline", props.bUnderline, "Underline")
            this.properties += this.toStr("underlineColor", props.underlineColor, "UnderlineColor")
            this.properties += this.toStr("bHorizontal", props.bHorizontal, "Horizontal")
            this.properties += this.toStr("letterSpacing", props.letterSpacing, "LetterSpacing")
            this.properties += this.toStr("backdropMode", props.backdropMode, "BackdropMode")
            this.properties += this.toStr("backdropColor", props.backdropColor, "BackdropColor")
            this.properties += this.toStr("backdropMarginTop", props.backdropMarginTop, "BackdropMarginTop")
            this.properties += this.toStr("backdropMarginBottom", props.backdropMarginBottom, "BackdropMarginBottom")
            this.properties += this.toStr("backdropMarginLeft", props.backdropMarginLeft, "BackdropMarginLeft")
            this.properties += this.toStr("backdropMarginRight", props.backdropMarginRight, "BackdropMarginRight")
        }
        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        update(symbol){
            let componentProps = ""
            if (!defined(symbol)){
                return componentProps
            }
            if (defined(symbol.alignMode)) {
                this.alignMode = symbol.alignMode
                componentProps += "AlignmentMode:" + this.alignMode + "|"
            }
            if (defined(symbol.rotateMode)) {
                this.rotateMode = symbol.rotateMode
                componentProps += "RotateMode:" + this.rotateMode + "|"
            }
            if (defined(symbol.zoomMode)) {
                this.zoomMode = symbol.zoomMode
                componentProps += "ZoomMode:" + this.zoomMode + "|"
            }
            if (defined(symbol.zoomLimit)) {
                this.zoomLimit = symbol.zoomLimit
                componentProps += "ZoomMaxDistance:" + this.zoomLimit + "|"
            }
            if (defined(symbol.minDistance)) {
                this.minDistance = symbol.minDistance
                componentProps += "MinVisibleDistance:" + this.minDistance + "|"
            }
            if (defined(symbol.maxDistance)) {
                this.maxDistance = symbol.maxDistance
                componentProps += "MaxVisibleDistance:" + this.maxDistance + "|"
            }
            if (defined(symbol.liftUp)) {
                this.liftUp = symbol.liftUp
                componentProps += "LiftUp:" + this.liftUp + "|"
            }
            if (defined(symbol.scale)) {
                this.scale = symbol.scale
                componentProps += "Scale:" + this.scale + "|"
            }
            if (defined(symbol.info)) {
                this.info = symbol.info
                componentProps += "Info:" + this.info + "|"
            }
            if (defined(symbol.fontUrl)) {
                this.fontUrl = symbol.fontUrl
                componentProps += "FontUrl:" + this.fontUrl + "|"
            }
            if (defined(symbol.size)) {
                this.size = symbol.size
                componentProps += "Size:" + this.size + "|";
            }
            if (defined(symbol.color)) {
                this.color = symbol.color
                componentProps += "Color:" + this.color.toStr() + "|"
            }
            if (defined(symbol.bOutline)) {
                this.bOutline = symbol.bOutline
                componentProps += "Outline:" + this.bOutline + "|"
            }
            if (defined(symbol.outlineColor)) {
                this.outlineColor = symbol.outlineColor
                componentProps += "OutlineColor:" + this.outlineColor.toStr() + "|"
            }
            if (defined(symbol.outlineWidth)) {
                this.outlineWidth = symbol.outlineWidth
                componentProps += "OutlineWidth:" + this.outlineWidth + "|"
            }
            if (defined(symbol.bEmbolden)) {
                this.bEmbolden = symbol.bEmbolden
                componentProps += "Embolden:" + this.bEmbolden + "|"
            }
            if (defined(symbol.bItalics)) {
                this.bItalics = symbol.bItalics
                componentProps += "Italics:" + this.bItalics + "|"
            }
            if (defined(symbol.bUnderline)) {
                this.bUnderline = symbol.bUnderline
                componentProps += "Underline:" + this.bUnderline + "|"
            }
            if (defined(symbol.underlineColor)) {
                this.underlineColor = symbol.underlineColor
                componentProps += "UnderlineColor:" + this.underlineColor.toStr() + "|"
            }
            if (defined(symbol.bHorizontal)) {
                this.bHorizontal = symbol.bHorizontal
                componentProps += "Horizontal:" + this.bHorizontal + "|"
            }
            if (defined(symbol.letterSpacing)) {
                this.letterSpacing = symbol.letterSpacing
                componentProps += "LetterSpacing:" + this.letterSpacing + "|"
            }
            if (defined(symbol.backdropMode)) {
                this.backdropMode = symbol.backdropMode
                componentProps += "BackdropMode:" + this.backdropMode + "|"
            }
            if (defined(symbol.backdropColor)) {
                this.backdropColor = symbol.backdropColor
                componentProps += "BackdropColor:" + this.backdropColor.toStr() + "|"
            }
            if (defined(symbol.backdropUrl)) {
                this.backdropUrl = symbol.backdropUrl
                componentProps += "BackdropImageUrl:" + this.backdropUrl + "|"
            }
            if (defined(symbol.backdropMarginTop)) {
                this.backdropMarginTop = symbol.backdropMarginTop
                componentProps += "BackdropMarginTop:" + this.backdropMarginTop + "|"
            }
            if (defined(symbol.backdropMarginBottom)) {
                this.backdropMarginBottom = symbol.backdropMarginBottom
                componentProps += "BackdropMarginBottom:" + this.backdropMarginBottom + "|"
            }
            if (defined(symbol.backdropMarginLeft)) {
                this.backdropMarginLeft = symbol.backdropMarginLeft
                componentProps += "BackdropMarginLeft:" + this.backdropMarginLeft + "|"
            }
            if (defined(symbol.backdropMarginRight)) {
                this.backdropMarginRight = symbol.backdropMarginRight
                componentProps += "BackdropMarginRight:" + this.backdropMarginRight + "|"
            }
            return componentProps
        }
    }
    return TextSymbol
})